import { ClientsApiService } from 'src/app/core/services/api/clients-api.service';
import { JsonObject, JsonProperty } from 'json2typescript';
import { City } from '../courts/city';
import { Charter } from '../data/charter';
import { Owner } from '../data/owner';
import { ProtectionClub } from '../data/protectionClub';
import { Representative } from '../data/representative';
import { Ship } from '../data/ship';
import { CaseSession, CaseSessionWrite } from '../hearings/caseSession';
import { ExpertSession } from '../hearings/expertSession';
import { LawyerLite } from '../lawyers/lawyer';
import { Service } from '../services/service';
import { CaseClient, CaseClientLite } from './caseClient';
import { CaseLawyer, PositionEnum } from './caseLawyer';
import { CaseLite } from './caseLite';
import { CaseOpponent, CaseOpponentLite } from './caseOpponent';
import { Claim } from './claim';
import { NgSelectProps } from '../ng-select-props';
import { SearchParams } from '../searchParams';

export enum SequenceEnum {
    'Marine' = 2,
    'Non-Marine' = 4,
    'Pandi' = 5,
    'Sudan' = 6
}

export enum SequenceEnumAr {
    'بحري' = 2,
    'مدني' = 4,
    'باندي' = 5,
    'السودان' = 6
}

export enum StatusEnum {
    OPENED = 1,
    PENDING = 2,
    CLOSED = 3,
    PENDINGREOPEN = 4,
    PENDINGCLOSE = 5

}

@JsonObject('CaseBase')
export class CaseBase {
    @JsonProperty('id', Number, true)
    id: number = undefined;

    @JsonProperty('status', Number, true)
    status: StatusEnum = undefined;

    @JsonProperty('sequence', Number)
    sequence: SequenceEnum = null;

    @JsonProperty('case_number', Number)
    caseNum = null;

    @JsonProperty('seq_number', String, true)
    seqNumber: string = null;

    @JsonProperty('subject', String, true)
    subject: string = null;

    @JsonProperty('closing_remarks', String, true)
    closingRemarks = null;

    @JsonProperty('open_date', String, true)
    openDate = null;

    @JsonProperty('reopen_date', String, true)
    reopenDate = null;

    @JsonProperty('closed_date', String, true)
    closedDate = null

    @JsonProperty('clients', [CaseClient], true)
    clients: CaseClient[] = [];

    @JsonProperty('opponents', [CaseOpponent], true)
    opponents: CaseOpponent[] = [];

    // @JsonProperty('contacts', [CaseClientContact], true)
    // contacts: CaseClientContact[] = [];

    @JsonProperty('lawyers', [CaseLawyer], true)
    lawyers: CaseLawyer[] = [];

    @JsonProperty('claims', [Claim], true)
    claims: Claim[] = [];

    // MARINE DATA
    @JsonProperty('owner_file_num', String, true)
    ownerFileNum = null;

    @JsonProperty('owner_club_file_num', String, true)
    ownerClubFileNum = null;

    @JsonProperty('departure_port', String, true)
    departurePort = null;

    @JsonProperty('departure_date', String, true)
    departureDate = null;

    @JsonProperty('arrival_port', String, true)
    arrivalPort = null;

    @JsonProperty('arrival_date', String, true)
    arrivalDate = null;

    @JsonProperty('shipment_desc', String, true)
    shipmentDesc = null;

    @JsonProperty('policy_num', String, true)
    policyNum = null;

    @JsonProperty('container_num', String, true)
    containerNum = null;

    @JsonProperty('club_notes', String, true)
    clubNotes = null;

    @JsonProperty('charter_file_num', String, true)
    charterFileNum = null;

    @JsonProperty('charter_club_file_num', String, true)
    charterClubFileNum = null;

    @JsonProperty('is_approved', Boolean, true)
    isApproved = null;

    addLawyer() {
        this.lawyers.push(new CaseLawyer());
    }

    removeLawyer(lawyer: CaseLawyer) {
        this.lawyers = this.lawyers.filter(l => l !== lawyer);
    }

    addClaim() {
        this.claims.push(new Claim());
    }

    removeClaim(claim: Claim) {
        this.claims = this.claims.filter(l => l !== claim);
    }

    addClient(clientsApi: ClientsApiService) {
        const client = new CaseClient();
        client.contactNgSelect = new NgSelectProps(clientsApi.getClientContacts, new SearchParams());
        this.clients.push(client);
    }

    removeClient(client: CaseClient) {
        this.clients = this.clients.filter(l => l !== client);
    }

    addOpponent() {
        this.opponents.push(new CaseOpponent());
    }

    removeOpponent(opponent: CaseOpponent) {
        this.opponents = this.opponents.filter(l => l !== opponent);
    }

    get fileNum() {
        return this.seqNumber;
    }

    get sequenceType() {
        return SequenceEnum[this.sequence];
    }

    get sequenceTypeAr() {
        return SequenceEnumAr[this.sequence];
    }

    get isMarine() {
        return this.sequence === SequenceEnum.Marine;
    }
    get isPandi() {
        return this.sequence === SequenceEnum.Pandi;
    }

    get isClosed() {
        return this.status === StatusEnum.CLOSED;
    }

    get isOpened() {
        return this.status === StatusEnum.OPENED;
    }

    get isNewCasePending() {
        return this.status === StatusEnum.PENDING;
    }

    get isClosedPending() {
        return this.status === StatusEnum.PENDINGCLOSE;
    }

    get isReopenPending() {
        return this.status === StatusEnum.PENDINGREOPEN;
    }

    get isPending() {
        return (this.status === StatusEnum.PENDING || this.status === StatusEnum.PENDINGREOPEN || this.status === StatusEnum.PENDINGCLOSE)
            && this.isApproved !== false;
    }

    get lawyersStr() {
        return this.lawyers.map(l => l.lawyer.name);
    }

    get lawyersArStr() {
        return this.lawyers.map(l => l.lawyer.nameAr);
    }

    get seniorLawyer() {
        return this.lawyers.find(l => l.position === PositionEnum.SENIOR)?.getName;
    }

    get juniorLawyers() {
        return this.lawyers.sort((a, b) => b.position - a.position)[0].lawyer;
    }
}

@JsonObject('Case')
export class Case extends CaseBase {
    @JsonProperty('related_cases', [CaseLite], true)
    relatedCases: CaseLite[] = [];

    @JsonProperty('last_session', CaseSession, true)
    lastSession: CaseSession = null;

    @JsonProperty('last_expert_session', ExpertSession, true)
    lastExpertSession: ExpertSession = null;

    @JsonProperty('active_timesheet', Number, true)
    activeTimesheet: number = undefined;

    @JsonProperty('timesheet', Number, true)
    timesheetId: number = undefined;

    @JsonProperty('reject_reason', String, true)
    rejectReason: string = null;

    @JsonProperty('created_by', String, true)
    createdBy: string = null;

    @JsonProperty('updated_date', String, true)
    updatedDate: string = null;

    @JsonProperty('created_date', String, true)
    createdDate: string = null;
    /******************
    * Marine File Data *
    *******************/
    @JsonProperty('ships', [Ship], true)
    ships: Ship[] | null = null;

    @JsonProperty('owner_club', ProtectionClub, true)
    ownerClub: ProtectionClub = null;

    @JsonProperty('owner', Owner, true)
    owner: Owner = null;

    @JsonProperty('charter_club', ProtectionClub, true)
    charterClub: ProtectionClub = null;

    @JsonProperty('charter', Charter, true)
    charter: Charter = null;

    @JsonProperty('representative', Representative, true)
    representative: Representative = null;

    @JsonProperty('nm_lawyer', LawyerLite, true)
    nmLawyer: LawyerLite = null;

    @JsonProperty('services', [Service], true)
    services: Service[] = [];

    @JsonProperty('session', CaseSession, true)
    session: CaseSession = null;

    @JsonProperty('city', City, true)
    city: City = null;

    init(clientsApi: ClientsApiService) {
        this.openDate = new Date().toISOString().slice(0, 10);
        if (this.clients.length === 0) {
            this.addClient(clientsApi);
        }
        if (this.lawyers.length === 0) {
            this.lawyers.push(new CaseLawyer());
        }
        this.session = new CaseSession();
        this.session.sequence = 1;
        this.session.lawsuits = [];
        this.session.lawyers = [];
    }

    showRejectReason() {
        return this.status === StatusEnum.PENDING && this.isApproved === false;
    }

    get clientStr() {
        // Youssef Hammoud, Softworx & Eldib Advocates (Defendant)
        return `${this.clients.map(c => c.name).join(', ')} (${this.clients.map(d => d.description ? d.description.getDescription : null)[0]})`;
    }

    get opponentStr() {
        // Youssef Hammoud, Softworx & Eldib Advocates (Defendant)
        if (!this.opponents.length) {
            return null;
        }
        return `${this.opponents.map(c => c.name).join(', ')} (${this.opponents.map(d => d.description ? d.description.getDescription : null)[0]})`;
    }


    // to match case with same properties(I used it in print files report from client files and lawyer files page)
    get clientsLite() {
        return this.clients.map(c => {
            const cLite = new CaseClientLite();
            cLite.client = c.client.fullName;
            cLite.description = c.description.getDescription;
            return cLite;
        });
    }
    get opponentsLite() {
        return this.opponents.map(opp => {
            const oppLite = new CaseOpponentLite();
            oppLite.opponent = opp.opponent.fullName;
            oppLite.description = opp.description.getDescription;
            return oppLite;
        });
    }

    get shipsStr() {
        if (!this.ships.length) {
            return null;
        }
        return `${this.ships.map(c => c.name).join(', ')} (${this.ships.map(s => s.nameFlag ? s.nameFlag : null)[0]})`;
    }

    get ownerStr() {
        return this.owner?.name;
    }

    get ownerClubStr() {
        return this.ownerClub?.name;
    }

    get charterClubStr() {
        return this.charterClub?.name;
    }

    get representativeStr() {
        return this.representative?.name;
    }

    get charterStr() {
        return this.charter?.name;
    }

    get cityStr() {
        return this.city.name || this.city.nameAr;
    }

    get nmLawyerStr(): string {
        return this.nmLawyer?.name || this.nmLawyer?.nameAr;
    }

    get lastSessionCourt() {
        return this.lastSession ? (this.lastSession.court ?
            this.lastSession.court.name + '\n' + this.lastSession.court.nameAr : null) : null;
    }

    get lastSessionCircle() {
        return this.lastSession ? (this.lastSession.circle ?
            this.lastSession.circle.name : null) : null;
    }

    get lastSessionDate() {
        return this.lastSession.sessionDate || null;
    }

    get lastSessionAttendees() {
        return this.lastSession.lawyers.map(l => l.lawyer.getName).join(', ');
    }

    get lastSessionCause() {
        return this.lastSession.getCause || null;
    }

    get lastSessionClaimNumbers() {
        return this.lastSession?.lawsuits;
    }

    get nextSession() {
        // TODO in backend and frontend
        return null;
    }

    get nextExpSession() {
        // TODO in backend and frontend
        return null;
    }

    get claimNumbers() {
        return this.lastSession?.lawsuits;
    }

    get court() {
        return this.lastSession?.court?.getName;
    }

    get circuit() {
        return this.lastSession?.circle?.getName;
    }

    get relationship(): Service {
        return this.services.find(s => s.level === 1);
    }
    get relFileType(): Service {
        return this.services.find(s => s.level === 2);
    }
    get fileServices(): Service[] {
        return this.services.filter(s => s.level === 3);
    }
}

@JsonObject('CaseWrite')
export class CaseWrite extends CaseBase {

    @JsonProperty('related_cases', [Number], true)
    relatedCases: number[] = [];

    @JsonProperty('session', CaseSessionWrite, true)
    session: CaseSessionWrite = null;

    /******************
    * Marine File Data *
    *******************/
    @JsonProperty('ships', [Number], true)
    ships: number[] = null;

    @JsonProperty('owner_club_id', Number, true)
    ownerClubId: number = undefined;

    @JsonProperty('owner_id', Number, true)
    ownerId: number = undefined;

    @JsonProperty('charter_club_id', Number, true)
    charterClubId: number = undefined;

    @JsonProperty('charter_id', Number, true)
    charterId: number = undefined;

    @JsonProperty('representative_id', Number, true)
    representativeId: number = undefined;

    @JsonProperty('nm_lawyer_id', Number, true)
    nmLawyerId: number = undefined;

    @JsonProperty('services', [Number], true)
    services: number[];

    @JsonProperty('city', Number, true)
    cityId: number = null;
}
